import { useEffect, useState } from "react";
import { Outlet, Link, useNavigate } from "react-router-dom";
import { Menu, X, Command, LogOut } from "lucide-react";
import { Sidebar } from "@/components/Sidebar";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SearchOverlay } from "@/components/SearchOverlay";
import { clearSession } from "@/lib/auth";

export const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const handleLogout = () => {
    clearSession();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "/" && !searchOpen) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag !== "input" && tag !== "textarea") {
          e.preventDefault();
          setSearchOpen(true);
        }
      }
    };
    const onOpen = () => setSearchOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("cms:open-search", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("cms:open-search", onOpen);
    };
  }, [searchOpen]);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background text-foreground">
      {/* Mobile top bar */}
      <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b-2 border-foreground bg-background sticky top-0 z-30">
        <Link to="/" className="font-display font-black text-xl" data-testid="mobile-logo">
          CMS<span className="text-brut-yellow">.</span>docs
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            data-testid="mobile-search-button"
            className="brut-border brut-shadow-sm h-10 w-10 flex items-center justify-center bg-background"
          >
            <Command className="w-4 h-4" strokeWidth={2.5} />
          </button>
          <ThemeToggle variant="mobile" />
          <button
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
            className="brut-border brut-shadow-sm h-10 w-10 flex items-center justify-center bg-background"
          >
            {mobileOpen ? (
              <X className="w-5 h-5" strokeWidth={2.5} />
            ) : (
              <Menu className="w-5 h-5" strokeWidth={2.5} />
            )}
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <div
        className={`lg:block ${mobileOpen ? "block" : "hidden"} lg:!block`}
      >
        <Sidebar onNavigate={() => setMobileOpen(false)} />
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Desktop top bar */}
        <div className="hidden lg:flex items-center justify-end gap-3 px-8 py-4 border-b-2 border-foreground sticky top-0 bg-background z-20">
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="search-trigger-button"
            className="flex items-center gap-3 brut-border brut-shadow-sm brut-hover bg-background pl-3 pr-2 py-1.5 text-sm w-72 justify-between"
          >
            <span className="flex items-center gap-2 text-muted-foreground">
              <Command className="w-4 h-4" strokeWidth={2.5} />
              Search tutorials…
            </span>
            <span className="flex items-center gap-1">
              <kbd className="kbd">⌘</kbd>
              <kbd className="kbd">K</kbd>
            </span>
          </button>
          <ThemeToggle />
          <button
            onClick={handleLogout}
            aria-label="Sign out"
            data-testid="logout-button"
            className="brut-border brut-shadow-sm brut-hover bg-background h-10 px-3 inline-flex items-center gap-2 text-sm font-semibold"
          >
            <LogOut className="w-4 h-4" strokeWidth={2.5} />
            Sign out
          </button>
        </div>

        <Outlet />
      </main>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};
